import { FC, useCallback, useEffect, useRef } from 'react';
import ElisaPlayer from '@elisaio/elisa-player';
import styles from './FlvPlayer.module.css';
import { useSocket } from '../../contexts/SocketContext';
import { STAGE_PLAY_VIDEO } from '../../consts/socket';
import { useSearchParams } from 'react-router-dom';

interface IProps {
  src: string;
  srcType: string;
  isPreRecorded: boolean;
  onVideoEnded?: () => void;
  onVideoTimeUpdated?: (time: number) => void;
  liveCatching: boolean;
  liveCatchingJumpThreshold: number;
  liveCatchingMaxLatency: number;
  liveCatchingMinRemain: number;
  useWorker: boolean;
  muted?: boolean;
}

export const FlvPlayer: FC<IProps> = (props) => {
  const {
    src,
    srcType,
    isPreRecorded,
    onVideoEnded: propOnVideoEnded,
    onVideoTimeUpdated: propOnVideoTimeUpdated,
    liveCatching,
    liveCatchingJumpThreshold,
    liveCatchingMaxLatency,
    liveCatchingMinRemain,
    useWorker,
    muted = false,
  } = props;

  const elisaPlayerRef = useRef<ElisaPlayer>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const playVideoRequested = useRef(false);
  const areSocketEventsSubscribed = useRef(false);

  const [searchParams] = useSearchParams();
  const destinationId = searchParams.get('did') || '';
  const startKey = `start_time_${destinationId}`;

  const { connected, subscribe } = useSocket();

  const initPlayer = useCallback(() => {
    const videoElem = videoRef.current;

    if (!videoElem) {
      return;
    }

    console.log('INITING PLAYER');

    let elisaPlayer: ElisaPlayer;

    if (elisaPlayerRef.current) {
      elisaPlayer = elisaPlayerRef.current;
      elisaPlayer.destroy();
    }

    elisaPlayer = new ElisaPlayer(
      {
        autoPlay: !isPreRecorded,
        element: videoElem,
        frameTracking: liveCatching,
        canRebuild: !isPreRecorded,
        trackingDelta: liveCatchingMaxLatency || 1.5,
        trackingJumpThreshold: liveCatchingJumpThreshold || 4.5,
        updateMinBuffer: liveCatchingMinRemain || 0.5,
      },
      {
        type: srcType,
        url: src,
      },
      {
        enableWorker: useWorker,
      },
    );

    elisaPlayerRef.current = elisaPlayer;

    playVideo();

    console.log('INITED PLAYER');
  }, [src]);

  useEffect(() => {
    initPlayer();
  }, [initPlayer]);

  useEffect(() => {
    return () => {
      if (elisaPlayerRef.current) {
        console.log('DESTROYING PLAYER ON UNMOUNT');
        elisaPlayerRef.current.destroy();
        elisaPlayerRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (!connected || areSocketEventsSubscribed.current || !isPreRecorded) {
      return;
    }

    console.log(`[FLV PLAYER] Subscribing to socket events`);

    subscribe(STAGE_PLAY_VIDEO, () => {
      console.log(`[FLV PLAYER] Socket Event Received: ${STAGE_PLAY_VIDEO}`);
      playVideoRequested.current = true;
      playVideo();
    });

    areSocketEventsSubscribed.current = true;
  }, [connected]);

  const playVideo = () => {
    if (!isPreRecorded || !videoRef.current || !elisaPlayerRef.current) {
      return;
    }

    const startTime = getStartTime();

    if (!startTime && !playVideoRequested.current) {
      return;
    }

    if (startTime) {
      const diff = Math.floor((Date.now() - startTime) / 1000);

      if (diff > 1) {
        console.log(`[FLV PLAYER] Seek to ${diff} seconds`);
        videoRef.current.currentTime = diff;
      }
    }

    console.log(`[FLV PLAYER] Play video`);
    elisaPlayerRef.current.play();

    if (!sessionStorage.getItem(startKey)) {
      sessionStorage.setItem(startKey, `${startTime || Date.now()}`);
    }
  };

  const getStartTime = () => {
    const storedStartTime = parseInt(sessionStorage.getItem(startKey) || '0');
    return storedStartTime || parseInt(searchParams.get('st') || '0');
  };

  const onVideoEnded = () => {
    propOnVideoEnded?.();
  };

  const onVideoTimeUpdate = () => {
    const videoElem = videoRef.current;

    if (!videoElem) {
      return;
    }

    propOnVideoTimeUpdated?.(videoElem.currentTime);
  };

  return (
    <video
      ref={videoRef}
      muted={muted}
      className={styles.video}
      onEnded={onVideoEnded}
      onTimeUpdate={onVideoTimeUpdate}
    />
  );
};
